import React, { useState, useEffect } from 'react';
import { IngredientCatalog } from '../models/diary';
import { CreateIngredientData } from '../services/ingredientService';
import { useIsMobile } from '../hooks/useIsMobile';

interface IngredientFormModalProps {
  open: boolean;
  item?: IngredientCatalog;
  onSave: (data: Partial<CreateIngredientData>) => Promise<void>;
  onCancel: () => void;
}

// Numeric nutrition fields rendered as a responsive grid. All per 100g.
const NUTRITION_FIELDS: { key: keyof IngredientFormState; label: string; required?: boolean }[] = [
  { key: 'calories', label: 'Calorías (kcal)', required: true },
  { key: 'proteins', label: 'Proteínas (g)' },
  { key: 'carbohydrates', label: 'Carbohidratos (g)' },
  { key: 'fats', label: 'Grasas (g)' },
  { key: 'fiber', label: 'Fibra (g)' },
  { key: 'water', label: 'Agua (ml)' },
];

interface IngredientFormState {
  name: string;
  calories: string;
  proteins: string;
  carbohydrates: string;
  fats: string;
  fiber: string;
  water: string;
}

const EMPTY: IngredientFormState = {
  name: '', calories: '', proteins: '', carbohydrates: '', fats: '', fiber: '', water: '',
};

const toFormState = (item?: IngredientCatalog): IngredientFormState => {
  if (!item) return { ...EMPTY };
  const numToStr = (v: number | null | undefined) => (v === null || v === undefined ? '' : String(v));
  return {
    name: item.name || '',
    calories: numToStr(item.calories),
    proteins: numToStr(item.proteins),
    carbohydrates: numToStr(item.carbohydrates),
    fats: numToStr(item.fats),
    fiber: numToStr(item.fiber),
    water: numToStr(item.water),
  };
};

const IngredientFormModal: React.FC<IngredientFormModalProps> = ({ open, item, onSave, onCancel }) => {
  const [formData, setFormData] = useState<IngredientFormState>({ ...EMPTY });
  const [errors, setErrors] = useState<Partial<Record<keyof IngredientFormState, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const isMobile = useIsMobile();
  const isEdit = !!item;

  useEffect(() => {
    if (open) {
      setFormData(toFormState(item));
      setErrors({});
      setSubmitError('');
    }
  }, [open, item]);

  const handleFieldChange = (field: keyof IngredientFormState, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    setSubmitError('');
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof IngredientFormState, string>> = {};
    if (!formData.name.trim()) newErrors.name = 'El nombre es requerido';
    if (!formData.calories.trim()) {
      newErrors.calories = 'Las calorías son requeridas';
    } else if (isNaN(Number(formData.calories)) || Number(formData.calories) < 0) {
      newErrors.calories = 'Valor inválido';
    }
    (['proteins', 'carbohydrates', 'fats', 'fiber', 'water'] as const).forEach(k => {
      if (formData[k].trim() && (isNaN(Number(formData[k])) || Number(formData[k]) < 0)) {
        newErrors[k] = 'Valor inválido';
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = (): CreateIngredientData => {
    const payload: CreateIngredientData = {
      name: formData.name.trim(),
      calories: Number(formData.calories),
    };
    (['proteins', 'carbohydrates', 'fats', 'fiber', 'water'] as const).forEach(k => {
      if (formData[k].trim()) payload[k] = Number(formData[k]);
    });
    return payload;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    setSubmitError('');
    try {
      await onSave(buildPayload());
    } catch (error: any) {
      if (error.response && typeof error.response === 'object') {
        const backendErrors: Partial<Record<keyof IngredientFormState, string>> = {};
        let hasFieldErrors = false;
        Object.keys(error.response).forEach((field) => {
          const messages = error.response[field];
          if (Array.isArray(messages) && messages.length > 0 && field in EMPTY) {
            hasFieldErrors = true;
            backendErrors[field as keyof IngredientFormState] = messages[0];
          }
        });
        if (hasFieldErrors) {
          setErrors(backendErrors);
          setSubmitError('Por favor corrige los errores en el formulario.');
        } else {
          setSubmitError(error.message || 'Error al guardar. Por favor intente nuevamente.');
        }
      } else {
        setSubmitError(error.message || 'Error al guardar. Por favor intente nuevamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
      background: 'rgba(0,0,0,0.18)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 1100, padding: isMobile ? '12px' : '20px',
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: isMobile ? '24px 20px' : '32px 40px',
        width: '100%', maxWidth: 560, maxHeight: '90vh', overflow: 'auto',
        boxShadow: '0 4px 32px rgba(0,0,0,0.13)',
      }}>
        <h2 style={{ marginTop: 0, marginBottom: 6, fontSize: 22, fontWeight: 600, color: '#1f2937' }}>
          {isEdit ? 'Editar Ingrediente' : 'Nuevo Ingrediente'}
        </h2>
        <p style={{ margin: '0 0 20px 0', fontSize: 13, color: '#6b7280' }}>
          Todos los valores nutricionales son por 100g.
        </p>

        {submitError && (
          <div style={{ background: '#fee2e2', color: '#dc2626', padding: '12px 16px', borderRadius: 8, marginBottom: 20, fontSize: 14 }}>
            {submitError}
          </div>
        )}

        {/* Nombre */}
        <label style={{ display: 'block', marginBottom: 18 }}>
          <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
            Nombre <span style={{ color: '#dc2626' }}>*</span>
          </span>
          <input
            value={formData.name}
            onChange={e => handleFieldChange('name', e.target.value)}
            style={{
              width: '100%', padding: '11px 14px',
              border: `1px solid ${errors.name ? '#dc2626' : '#cbd5e1'}`,
              borderRadius: 6, fontSize: 15, outline: 'none', boxSizing: 'border-box',
            }}
            placeholder="Ej: Pechuga de pollo"
          />
          {errors.name && <span style={{ color: '#dc2626', fontSize: 13, marginTop: 6, display: 'block' }}>{errors.name}</span>}
        </label>

        {/* Nutrition grid */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)', gap: 14, marginBottom: 28 }}>
          {NUTRITION_FIELDS.map(({ key, label, required }) => (
            <label key={key} style={{ display: 'block' }}>
              <span style={{ fontWeight: 600, marginBottom: 6, display: 'block', fontSize: 13, color: '#374151' }}>
                {label} {required && <span style={{ color: '#dc2626' }}>*</span>}
              </span>
              <input
                type="number"
                min="0"
                step="any"
                value={formData[key]}
                onChange={e => handleFieldChange(key, e.target.value)}
                style={{
                  width: '100%', padding: '10px 12px',
                  border: `1px solid ${errors[key] ? '#dc2626' : '#cbd5e1'}`,
                  borderRadius: 6, fontSize: 14, outline: 'none', boxSizing: 'border-box',
                }}
                placeholder="0"
              />
              {errors[key] && <span style={{ color: '#dc2626', fontSize: 12, marginTop: 4, display: 'block' }}>{errors[key]}</span>}
            </label>
          ))}
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', paddingTop: 8 }}>
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            style={{
              background: '#f3f4f6', border: 'none', borderRadius: 6, padding: '11px 24px',
              fontWeight: 600, fontSize: 15, cursor: isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isSubmitting ? 0.6 : 1, color: '#374151',
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={isSubmitting}
            style={{
              background: isSubmitting ? '#81c9c4' : '#38b2ac', color: '#fff', border: 'none',
              borderRadius: 6, padding: '11px 24px', fontWeight: 600, fontSize: 15,
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
            }}
          >
            {isSubmitting ? 'Guardando...' : isEdit ? 'Guardar Cambios' : 'Crear Ingrediente'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IngredientFormModal;
